import { styled, Typography } from '@mui/material';
import DialogCloseSvgIcon from '../../../assets/svg/dialog/DialogCloseSvgIcon';

import { useEffect, useState } from 'react';
import SettingSequenceInput from './SettingSequenceInput';
import Input from '../../../components/TextField/Input';

interface SettingItemProp {
  handleCloseInputItem: (type: string) => void;
  handleOnChange: (value: string | string[]) => void;
  onClose?: () => void;
  value: string;
}

interface SequenceInfoProp {
  size: number;
  type: string;
}

const SpanRequire = styled('span')`
  color: ${({ theme }) => theme.color.highlight};
`;

const Title = styled(Typography)(({ theme }) => ({
  ...theme.externalTypography.body3,
  color: theme.color.secondary.$60,
}));

const SettingInput: React.FC<SettingItemProp> = ({
  handleCloseInputItem,
  handleOnChange,
  value,
}) => {
  const [input, setInput] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isSequence, setIsSequence] = useState<boolean>(false);
  const [sequenceInfo, setSequenceInfo] = useState<SequenceInfoProp>({
    size: 0,
    type: '',
  });

  const type = value ? value.split(' - ')[1] : '';

  useEffect(() => {
    if (type) {
      let temp: string = '';
      const sequenceRegexp = /([\w]+)\[(\d+)\]/;
      const sequenceMatch = type.match(sequenceRegexp);

      if (sequenceMatch) {
        setIsSequence(true);
        temp = 'sequence';
        setSequenceInfo({ type: sequenceMatch[1], size: +sequenceMatch[2] });
      } else {
        // Byte16, Char(16) -> output: ["Byte", "16"]
        const typeRegexp = /([A-Za-z]+)\s*\(?\s*(\d+)\s*\)?/;
        const match = type.match(typeRegexp);
        let size;
        let dataType = '';
        if (match) {
          dataType = match[1];
          size = +match[2];
        }

        switch (dataType) {
          case 'Byte': {
            const invalidChars = input.match(/[^0-9A-F]/gi);
            if (input === '') {
              temp = `${type}: Empty Input`;
            } else if (invalidChars) {
              temp = `${type}: Invalid character '${invalidChars[invalidChars.length - 1]}'`;
            } else if (input.length % 2 !== 0) {
              temp = `${type}: Non-even number of characters`;
            } else if (input.length > size * 2) {
              temp = `${type}: Invalid Size. Now is ${input.length / 2} byte(s) but max length is ${size} byte(s)`;
            }
            break;
          }
          case 'Char': {
            // const invalidChars = input.match(/[^0-9A-Z]/gi);
            if (input === '') {
              temp = `${type}: Empty Input`;
            }
            // else if (invalidChars) {
            //   temp = `${type}: Invalid character '${invalidChars[invalidChars.length - 1]}'`;
            // }
            else if (input.length > size) {
              temp = `${type}: Invalid Size. Now is ${input.length} character(s) but max length is ${size} character(s)`;
            }
            break;
          }
          case 'Int': {
            const invalidChars = input.match(/[^0-9-]/g);
            if (input === '') {
              temp = `${type}: Empty Input`;
            } else if (invalidChars) {
              temp = `${type}: Invalid character '${invalidChars[invalidChars.length - 1]}'`;
            } else if (
              !(
                -Math.pow(2, size) / 2 <= +input &&
                +input < Math.pow(2, size) / 2
              )
            ) {
              temp = `${type}: ${input} is out of range [-${Math.pow(2, size) / 2}, ${Math.pow(2, size) / 2 - 1}]`;
            }
            break;
          }
          case 'UInt': {
            const invalidChars = input.match(/[^0-9]/g);
            if (input === '') {
              temp = `${type}: Empty Input`;
            } else if (invalidChars) {
              temp = `${type}: Invalid character '${invalidChars[invalidChars.length - 1]}'`;
            } else if (!(0 <= +input && +input < Math.pow(2, size) - 1)) {
              temp = `${type}: ${input} is out of range [0, ${Math.pow(2, size) - 1}]`;
            }
            break;
          }
          default:
            //* for edge cases detection
            temp = `Invalid dataType ${type}`;
            break;
        }
      }
      setErrorMsg(temp);
    }
  }, [input]);

  return (
    <>
      {isSequence === false && (
        <div>
          <span>
            <Title>
              {value} <SpanRequire>*</SpanRequire>
            </Title>
          </span>
          <div
            className="container-input"
            style={{ display: 'flex', flexDirection: 'row' }}
          >
            <Input
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setInput(e.target.value);
                handleOnChange(e.target.value);
              }}
              value={input}
              style={{ flex: 1 }}
              error={errorMsg !== ''}
              errorMessage={errorMsg}
            />

            <DialogCloseSvgIcon
              className="button"
              onClick={() => handleCloseInputItem(value)}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </div>
      )}
      {isSequence && (
        <SettingSequenceInput
          name={value ?? ''}
          size={sequenceInfo.size}
          type={sequenceInfo.type}
          handleOnChange={(value: string[]) => handleOnChange(value)}
          handleCloseInputItem={handleCloseInputItem}
        />
      )}
    </>
  );
};

export default SettingInput;
